/**
 * Site-wide "you seem to be in Australia" popup for the US store.
 *
 * Detection is IP-based via a third-party geo-IP lookup, never the browser
 * Geolocation API (no permission prompt).
 *
 * Persistence: choosing "Go to Australia site" or "Stay on US site" sets a
 * 5-minute cookie (matches the wireframe's "we remember your choice for 5
 * minutes") so the popup doesn't ask again for that visitor. The × close is a
 * lighter, uncommitted dismissal — it does not set that cookie, so the popup
 * can still reappear on a later page load if the visitor never made an
 * explicit choice.
 */
if (!customElements.get("au-geo-popup")) {
  customElements.define(
    "au-geo-popup",
    class AuGeoPopup extends HTMLElement {
      static GEO_ENDPOINT = "https://get.geojs.io/v1/ip/country.json";
      static GEO_TIMEOUT_MS = 3000;
      static COOKIE_NAME = "xtresse_au_geo_choice";
      static COOKIE_MINUTES = 5;
      // Per-session cache of a definitive non-AU geo result, so the API isn't
      // hit on every pageview for non-AU visitors who never set a choice cookie.
      // sessionStorage (not a cookie) so a new session re-checks — a visitor who
      // switches to an AU VPN or travels there is still detected on their next visit.
      static CHECK_KEY = "xtresse_au_geo_checked";
      static FOCUSABLE_SELECTOR = [
        "a[href]",
        "button:not([disabled])",
        "input:not([disabled])",
        "select:not([disabled])",
        "textarea:not([disabled])",
        '[tabindex]:not([tabindex="-1"])',
      ].join(",");

      connectedCallback() {
        this.onClick = this.onClick.bind(this);
        this.onKeydown = this.onKeydown.bind(this);

        this.addEventListener("click", this.onClick);

        if (this.forcedCountry() === "AU") {
          this.show();
          return;
        }
        if (this.getCookie(AuGeoPopup.COOKIE_NAME) || this.geoChecked()) return;

        this.checkGeo();
      }

      disconnectedCallback() {
        this.removeEventListener("click", this.onClick);
        document.removeEventListener("keydown", this.onKeydown);
        this.abortController?.abort();
        this.restoreBackground();
        document.body.classList.remove("au-geo-popup-open");
      }

      async checkGeo() {
        const controller = new AbortController();
        this.abortController = controller;
        const timeout = setTimeout(
          () => controller.abort(),
          AuGeoPopup.GEO_TIMEOUT_MS,
        );

        try {
          const response = await fetch(AuGeoPopup.GEO_ENDPOINT, {
            signal: controller.signal,
          });
          if (!response.ok) return;

          const data = await response.json();
          if (data && data.country === "AU") {
            this.show();
          } else if (data && data.country) {
            // Definitive non-AU answer: cache for the session so we don't
            // re-hit the geo API on subsequent pageviews.
            this.markGeoChecked();
          }
        } catch (error) {
          // Network error, timeout, or blocked request: fail silently.
          // The popup simply doesn't show — it must never break the page.
        } finally {
          clearTimeout(timeout);
          if (this.abortController === controller) {
            this.abortController = null;
          }
        }
      }

      show() {
        if (!this.hidden) return;

        this.previouslyFocusedElement = document.activeElement;
        this.removeAttribute("hidden");
        document.body.classList.add("au-geo-popup-open");
        this.disableBackground();
        document.addEventListener("keydown", this.onKeydown);
        this.querySelector("[data-au-geo-dismiss]")?.focus();
      }

      hide() {
        if (this.hidden) return;

        this.setAttribute("hidden", "");
        document.body.classList.remove("au-geo-popup-open");
        document.removeEventListener("keydown", this.onKeydown);
        this.restoreBackground();

        if (this.previouslyFocusedElement?.isConnected) {
          this.previouslyFocusedElement.focus();
        }
        this.previouslyFocusedElement = null;
      }

      setCookie(name, value, minutes) {
        const expires = new Date();
        expires.setTime(expires.getTime() + minutes * 60 * 1000);
        document.cookie = `${name}=${value};expires=${expires.toUTCString()};path=/;SameSite=Lax;Secure`;
      }

      forcedCountry() {
        try {
          return new URLSearchParams(window.location.search)
            .get("xt_geo")
            ?.toUpperCase();
        } catch (error) {
          return null;
        }
      }

      disableBackground() {
        this.inertElements = Array.from(document.body.children).filter(
          (element) => element !== this && !element.inert,
        );
        this.inertElements.forEach((element) => {
          element.inert = true;
        });
      }

      restoreBackground() {
        this.inertElements?.forEach((element) => {
          element.inert = false;
        });
        this.inertElements = [];
      }

      geoChecked() {
        // sessionStorage can throw (privacy mode, disabled storage); never break.
        try {
          return sessionStorage.getItem(AuGeoPopup.CHECK_KEY);
        } catch (error) {
          return null;
        }
      }

      markGeoChecked() {
        try {
          sessionStorage.setItem(AuGeoPopup.CHECK_KEY, "1");
        } catch (error) {
          // Ignore: worst case we re-check on the next pageview.
        }
      }

      getCookie(name) {
        const nameEQ = `${name}=`;
        const cookies = document.cookie.split(";");
        for (let cookie of cookies) {
          cookie = cookie.trim();
          if (cookie.indexOf(nameEQ) === 0) {
            return cookie.substring(nameEQ.length);
          }
        }
        return null;
      }

      onClick(event) {
        if (event.target.closest("[data-au-geo-go]")) {
          // Real <a href>: let navigation proceed after the cookie is set.
          this.setCookie(
            AuGeoPopup.COOKIE_NAME,
            "au",
            AuGeoPopup.COOKIE_MINUTES,
          );
          return;
        }

        if (event.target.closest("[data-au-geo-stay]")) {
          this.setCookie(
            AuGeoPopup.COOKIE_NAME,
            "stay",
            AuGeoPopup.COOKIE_MINUTES,
          );
          this.hide();
          return;
        }

        if (event.target.closest("[data-au-geo-dismiss]")) {
          event.preventDefault();
          this.hide();
        }
      }

      onKeydown(event) {
        if (this.hidden) return;

        if (event.key === "Escape") {
          event.preventDefault();
          this.hide();
          return;
        }

        if (event.key !== "Tab") return;

        const focusableElements = Array.from(
          this.querySelectorAll(AuGeoPopup.FOCUSABLE_SELECTOR),
        );
        if (!focusableElements.length) {
          event.preventDefault();
          return;
        }

        const firstElement = focusableElements[0];
        const lastElement = focusableElements[focusableElements.length - 1];

        if (event.shiftKey && document.activeElement === firstElement) {
          event.preventDefault();
          lastElement.focus();
        } else if (
          !event.shiftKey &&
          document.activeElement === lastElement
        ) {
          event.preventDefault();
          firstElement.focus();
        } else if (!this.contains(document.activeElement)) {
          event.preventDefault();
          firstElement.focus();
        }
      }
    },
  );
}
